/**
 * 3MF Export — generates a .3mf file (ZIP of XML) from mesh data.
 * 3MF is the standard format for 3D printing (Microsoft, HP, Stratasys).
 */

import type { MeshResult } from "./scriptRunner";

function escapeXml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}

function safeNum(v: number): string {
  if (!isFinite(v)) return "0";
  return String(v);
}

function generateModelXml(mesh: MeshResult, name: string): string {
  const verts = mesh.vertices;
  const idx = mesh.indices;

  if (verts.length === 0 || idx.length === 0) {
    throw new Error("Cannot export empty geometry to 3MF");
  }

  let vertexXml = "";
  for (let i = 0; i < verts.length; i += 3) {
    vertexXml += `        <vertex x="${safeNum(verts[i])}" y="${safeNum(verts[i + 1])}" z="${safeNum(verts[i + 2])}" />\n`;
  }

  let triangleXml = "";
  for (let i = 0; i < idx.length; i += 3) {
    triangleXml += `        <triangle v1="${idx[i]}" v2="${idx[i + 1]}" v3="${idx[i + 2]}" />\n`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<model unit="millimeter" xml:lang="en-US"
  xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02">
  <metadata name="Title">${escapeXml(name)}</metadata>
  <metadata name="Designer">cad-plm</metadata>
  <resources>
    <object id="1" type="model" name="${escapeXml(name)}">
      <mesh>
        <vertices>
${vertexXml}        </vertices>
        <triangles>
${triangleXml}        </triangles>
      </mesh>
    </object>
  </resources>
  <build>
    <item objectid="1" />
  </build>
</model>`;
}

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml" />
  <Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml" />
</Types>`;

const RELS = `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Target="/3D/3dmodel.model" Id="rel0"
    Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel" />
</Relationships>`;

/**
 * Create a 3MF file as a Blob from mesh data.
 * Uses the JSZip-free approach: manually construct the ZIP.
 * For simplicity, we use uncompressed entries (STORE method).
 */
export async function export3MF(mesh: MeshResult, name: string): Promise<Blob> {
  const modelXml = generateModelXml(mesh, name);
  const enc = new TextEncoder();

  const files: { path: string; data: Uint8Array }[] = [
    { path: "[Content_Types].xml", data: enc.encode(CONTENT_TYPES) },
    { path: "_rels/.rels", data: enc.encode(RELS) },
    { path: "3D/3dmodel.model", data: enc.encode(modelXml) },
  ];

  // Build a minimal ZIP file (STORE method, no compression)
  const parts: Uint8Array[] = [];
  const centralDir: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const pathBytes = enc.encode(file.path);
    // Local file header (30 bytes + path + data)
    const header = new ArrayBuffer(30);
    const hv = new DataView(header);
    hv.setUint32(0, 0x04034b50, true); // signature
    hv.setUint16(4, 20, true);          // version needed
    hv.setUint16(6, 0, true);           // flags
    hv.setUint16(8, 0, true);           // compression: STORE
    hv.setUint16(10, 0, true);          // mod time
    hv.setUint16(12, 0, true);          // mod date
    hv.setUint32(14, crc32(file.data), true);
    hv.setUint32(18, file.data.length, true); // compressed size
    hv.setUint32(22, file.data.length, true); // uncompressed size
    hv.setUint16(26, pathBytes.length, true);
    hv.setUint16(28, 0, true);          // extra field length

    parts.push(new Uint8Array(header), pathBytes, file.data);

    // Central directory entry (46 bytes + path)
    const cdEntry = new ArrayBuffer(46);
    const cv = new DataView(cdEntry);
    cv.setUint32(0, 0x02014b50, true);  // signature
    cv.setUint16(4, 20, true);           // version made by
    cv.setUint16(6, 20, true);           // version needed
    cv.setUint16(8, 0, true);            // flags
    cv.setUint16(10, 0, true);           // compression
    cv.setUint16(12, 0, true);           // mod time
    cv.setUint16(14, 0, true);           // mod date
    cv.setUint32(16, crc32(file.data), true);
    cv.setUint32(20, file.data.length, true);
    cv.setUint32(24, file.data.length, true);
    cv.setUint16(28, pathBytes.length, true);
    cv.setUint16(30, 0, true);           // extra length
    cv.setUint16(32, 0, true);           // comment length
    cv.setUint16(34, 0, true);           // disk number start
    cv.setUint16(36, 0, true);           // internal attributes
    cv.setUint32(38, 0, true);           // external attributes
    cv.setUint32(42, offset, true);      // local header offset
    centralDir.push(new Uint8Array(cdEntry), pathBytes);

    offset += 30 + pathBytes.length + file.data.length;
  }

  const cdOffset = offset;
  let cdSize = 0;
  for (const cd of centralDir) cdSize += cd.length;

  // End of central directory (22 bytes)
  const eocd = new ArrayBuffer(22);
  const ev = new DataView(eocd);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, cdOffset, true);
  ev.setUint16(20, 0, true);

  // Concatenate all parts into a single ArrayBuffer for reliable Blob creation
  const totalSize = parts.reduce((s, p) => s + p.length, 0) + cdSize + 22;
  const result = new Uint8Array(totalSize);
  let pos = 0;
  for (const p of parts) { result.set(p, pos); pos += p.length; }
  for (const cd of centralDir) { result.set(cd, pos); pos += cd.length; }
  result.set(new Uint8Array(eocd), pos);

  return new Blob([result], { type: "model/3mf" });
}

/** CRC-32 lookup table */
const CRC_TABLE = new Uint32Array(256);
for (let i = 0; i < 256; i++) {
  let c = i;
  for (let j = 0; j < 8; j++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  CRC_TABLE[i] = c;
}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}
